import React, { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import QRCode from "qrcode";
import { useAuth } from "./AuthContext";
import "./LoginPage.css";

const STEP_CREDENTIALS = "credentials";
const STEP_SETUP_TOTP  = "setup_totp";   // first login: scan QR + confirm
const STEP_TOTP        = "totp";          // returning login: enter code

export default function LoginPage() {
  const { verifyCredentials, verifyTotp, setupTotp } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [step, setStep]               = useState(STEP_CREDENTIALS);
  const [username, setUsername]       = useState("");
  const [password, setPassword]       = useState("");
  const [totpDigits, setTotpDigits]   = useState(["", "", "", "", "", ""]);
  const [error, setError]             = useState("");
  const [loading, setLoading]         = useState(false);

  // 2FA setup state (first-login only)
  const [setupSecret, setSetupSecret] = useState("");
  const [setupUri, setSetupUri]       = useState("");
  const [qrDataUrl, setQrDataUrl]     = useState("");

  const totpRefs    = useRef([]);
  const usernameRef = useRef(null);

  const redirectTo = location.state?.from?.pathname || "/";

  useEffect(() => {
    if (step === STEP_CREDENTIALS && usernameRef.current) {
      usernameRef.current.focus();
    }
  }, [step]);

  // Generate QR data URL whenever we enter the setup step
  useEffect(() => {
    if (step === STEP_SETUP_TOTP && setupUri) {
      QRCode.toDataURL(setupUri, { width: 200, margin: 2 })
        .then(setQrDataUrl)
        .catch(() => setQrDataUrl(""));
    }
  }, [step, setupUri]);

  // ── Step 1: credentials ────────────────────────────────────────────────────
  async function handleCredentialsSubmit(e) {
    e.preventDefault();
    setError("");
    setLoading(true);

    const result = await verifyCredentials(username.trim(), password);
    setLoading(false);

    if (!result.success) {
      setError(result.error);
      return;
    }

    if (result.requiresSetup) {
      setSetupSecret(result.totpSecret);
      setSetupUri(result.totpUri);
      setStep(STEP_SETUP_TOTP);
    } else {
      setStep(STEP_TOTP);
    }
  }

  // ── Step 2a: TOTP setup (first login) ─────────────────────────────────────
  async function handleSetupTotpSubmit(e) {
    e.preventDefault();
    const code = totpDigits.join("");
    if (code.length < 6) {
      setError("Please enter the complete 6-digit code.");
      return;
    }

    setError("");
    setLoading(true);
    const result = await setupTotp(code, username.trim(), setupSecret);
    setLoading(false);

    if (!result.success) {
      setError(result.error);
      setTotpDigits(["", "", "", "", "", ""]);
      totpRefs.current[0]?.focus();
      return;
    }

    navigate(redirectTo, { replace: true });
  }

  // ── Step 2b: TOTP verify (returning login) ─────────────────────────────────
  async function handleTotpSubmit(e) {
    e.preventDefault();
    const code = totpDigits.join("");
    if (code.length < 6) {
      setError("Please enter the complete 6-digit code.");
      return;
    }

    setError("");
    setLoading(true);
    const result = await verifyTotp(code, username.trim());
    setLoading(false);

    if (!result.success) {
      setError(result.error);
      setTotpDigits(["", "", "", "", "", ""]);
      totpRefs.current[0]?.focus();
      return;
    }

    navigate(redirectTo, { replace: true });
  }

  // ── Shared TOTP input helpers ──────────────────────────────────────────────
  function handleTotpChange(index, value) {
    if (!/^\d*$/.test(value)) return;
    const next = [...totpDigits];
    next[index] = value.slice(-1);
    setTotpDigits(next);
    setError("");
    if (value && index < 5) {
      totpRefs.current[index + 1]?.focus();
    }
  }

  function handleTotpKeyDown(index, e) {
    if (e.key === "Backspace" && !totpDigits[index] && index > 0) {
      totpRefs.current[index - 1]?.focus();
    }
  }

  function handleTotpPaste(e) {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    const next = ["", "", "", "", "", ""];
    for (let i = 0; i < pasted.length; i++) next[i] = pasted[i];
    setTotpDigits(next);
    totpRefs.current[Math.min(pasted.length, 5)]?.focus();
  }

  function handleBackToCredentials() {
    setStep(STEP_CREDENTIALS);
    setTotpDigits(["", "", "", "", "", ""]);
    setSetupSecret("");
    setSetupUri("");
    setQrDataUrl("");
    setError("");
  }

  const buildTotpRef = useCallback((el, i) => { totpRefs.current[i] = el; }, []);
  const isOnStep2    = step !== STEP_CREDENTIALS;

  return (
    <div className="login-page">
      <div className="login-card">
        <div className="login-logo">
          <span className="login-logo-text">Proponent</span>
        </div>

        {/* ── Step 1: Credentials ── */}
        {step === STEP_CREDENTIALS && (
          <>
            <h2 className="login-title">Sign in</h2>
            <p className="login-subtitle">Enter your credentials to continue</p>

            <form onSubmit={handleCredentialsSubmit} noValidate>
              <div className="login-field">
                <label htmlFor="username">Username</label>
                <input
                  id="username"
                  ref={usernameRef}
                  type="text"
                  autoComplete="username"
                  value={username}
                  onChange={(e) => { setUsername(e.target.value); setError(""); }}
                  placeholder="Your username"
                  disabled={loading}
                  required
                />
              </div>

              <div className="login-field">
                <label htmlFor="password">Password</label>
                <input
                  id="password"
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => { setPassword(e.target.value); setError(""); }}
                  placeholder="Your password"
                  disabled={loading}
                  required
                />
              </div>

              {error && <p className="login-error">{error}</p>}

              <button
                type="submit"
                className="login-btn-primary"
                disabled={loading || !username || !password}
              >
                {loading ? "Checking…" : "Continue"}
              </button>
            </form>
          </>
        )}

        {/* ── Step 2a: First-login 2FA setup ── */}
        {step === STEP_SETUP_TOTP && (
          <>
            <h2 className="login-title">Set up two-factor authentication</h2>
            <p className="login-subtitle">
              Scan the QR code with your authenticator app, then enter the 6-digit code to confirm
            </p>

            {qrDataUrl ? (
              <div className="totp-qr-wrapper">
                <img src={qrDataUrl} alt="Scan with your authenticator app" className="totp-qr" />
              </div>
            ) : (
              <p className="login-hint">Loading QR code…</p>
            )}

            <details className="totp-manual">
              <summary>Can't scan? Enter the key manually</summary>
              <p className="totp-manual-key">{setupSecret}</p>
            </details>

            <form onSubmit={handleSetupTotpSubmit} noValidate>
              <div className="totp-inputs" onPaste={handleTotpPaste}>
                {totpDigits.map((digit, i) => (
                  <input
                    key={`setup-digit-${i}`}
                    ref={(el) => buildTotpRef(el, i)}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    autoFocus={i === 0}
                    autoComplete="one-time-code"
                    onChange={(e) => handleTotpChange(i, e.target.value)}
                    onKeyDown={(e) => handleTotpKeyDown(i, e)}
                    disabled={loading}
                    className="totp-cell"
                    aria-label={`Digit ${i + 1}`}
                  />
                ))}
              </div>

              {error && <p className="login-error">{error}</p>}

              <button
                type="submit"
                className="login-btn-primary"
                disabled={loading || totpDigits.join("").length < 6}
              >
                {loading ? "Saving…" : "Confirm & sign in"}
              </button>

              <button
                type="button"
                className="login-btn-secondary"
                onClick={handleBackToCredentials}
                disabled={loading}
              >
                ← Back
              </button>
            </form>

            <p className="login-hint">
              Use <strong>Google Authenticator</strong>, <strong>Authy</strong>, or any TOTP app.
              After scanning, you'll use this app every time you sign in.
            </p>
          </>
        )}

        {/* ── Step 2b: Returning login TOTP verify ── */}
        {step === STEP_TOTP && (
          <>
            <h2 className="login-title">Two-factor authentication</h2>
            <p className="login-subtitle">
              Enter the 6-digit code from your authenticator app
            </p>

            <form onSubmit={handleTotpSubmit} noValidate>
              <div className="totp-inputs" onPaste={handleTotpPaste}>
                {totpDigits.map((digit, i) => (
                  <input
                    key={`totp-digit-${i}`}
                    ref={(el) => buildTotpRef(el, i)}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    autoFocus={i === 0}
                    autoComplete="one-time-code"
                    onChange={(e) => handleTotpChange(i, e.target.value)}
                    onKeyDown={(e) => handleTotpKeyDown(i, e)}
                    disabled={loading}
                    className="totp-cell"
                    aria-label={`Digit ${i + 1}`}
                  />
                ))}
              </div>

              {error && <p className="login-error">{error}</p>}

              <button
                type="submit"
                className="login-btn-primary"
                disabled={loading || totpDigits.join("").length < 6}
              >
                {loading ? "Verifying…" : "Sign in"}
              </button>

              <button
                type="button"
                className="login-btn-secondary"
                onClick={handleBackToCredentials}
                disabled={loading}
              >
                ← Back
              </button>
            </form>

            <p className="login-hint">
              Open Google Authenticator, Authy, or any TOTP app and enter the
              code shown for <strong>Proponent</strong>.
            </p>
          </>
        )}

        {/* ── Step indicator ── */}
        <div className="login-step-indicator">
          <span className={`login-step ${isOnStep2 ? "done" : "active"}`}>1</span>
          <span className="login-step-line" />
          <span className={`login-step ${isOnStep2 ? "active" : ""}`}>2</span>
        </div>
      </div>
    </div>
  );
}
